import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * HMRC fraud-prevention client-data payload (CONTRACT §1). Serialised to
 * base64(JSON) and sent as the `X-JL-Client-Data` header on every request to
 * our API origin. Treated as untrusted input by the backend.
 */
export interface ClientData {
  deviceId: string;
  timezone: string;
  userAgent: string;
  plugins: string[];
  doNotTrack: boolean;
  screenWidth: number;
  screenHeight: number;
  colourDepth: number;
  scalingFactor: number;
  windowWidth: number;
  windowHeight: number;
  frontendVersion: string;
}

const DEVICE_ID_STORAGE_KEY = 'jl-hmrc-device-id';

/** CONTRACT §1: keep the base64 header under 4 KB. */
const MAX_HEADER_BYTES = 4096;

/**
 * Assembles the HMRC fraud-prevention client-data snapshot.
 *
 * Static, session-stable fields (device id, timezone, user agent, plugins,
 * screen metrics) are computed lazily once and cached. The window dimensions
 * are RE-READ on every `snapshot()` call because the window is resizable and
 * HMRC expects the live values.
 */
@Injectable({ providedIn: 'root' })
export class HmrcClientDataService {
  private cachedDeviceId: string | null = null;
  private cachedStatic: Omit<ClientData, 'windowWidth' | 'windowHeight'> | null = null;

  /** base64(JSON) of the client-data payload, capped at 4 KB per CONTRACT §1. */
  snapshot(): string {
    const data: ClientData = {
      ...this.staticFields(),
      // Re-read every call — the window is resizable.
      windowWidth: this.readWindowWidth(),
      windowHeight: this.readWindowHeight(),
    };

    return this.encodeWithinCap(data);
  }

  /**
   * A stable per-DEVICE id (not per-user): persisted in localStorage, created on
   * first access, and never cleared on sign-out. Falls back to an in-memory id
   * when storage is unavailable so the header is still valid.
   */
  private deviceId(): string {
    if (this.cachedDeviceId) {
      return this.cachedDeviceId;
    }

    const stored = this.readStoredDeviceId();
    if (stored) {
      this.cachedDeviceId = stored;
      return stored;
    }

    const generated = this.newUuid();
    this.writeStoredDeviceId(generated);
    this.cachedDeviceId = generated;
    return generated;
  }

  private staticFields(): Omit<ClientData, 'windowWidth' | 'windowHeight'> {
    if (this.cachedStatic) {
      // deviceId is memoised separately but stitched in so a mid-session storage
      // write (first access) is reflected without recomputing screen/plugins.
      return { ...this.cachedStatic, deviceId: this.deviceId() };
    }

    const screen = typeof window !== 'undefined' ? window.screen : undefined;

    this.cachedStatic = {
      deviceId: this.deviceId(),
      timezone: this.formatTimezone(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      plugins: this.readPlugins(),
      doNotTrack: typeof navigator !== 'undefined' && navigator.doNotTrack === '1',
      screenWidth: screen?.width ?? 0,
      screenHeight: screen?.height ?? 0,
      colourDepth: screen?.colorDepth ?? 0,
      scalingFactor: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
      frontendVersion: environment.version,
    };

    return this.cachedStatic;
  }

  /**
   * Formats the local timezone offset as "UTC±HH:MM". Note the INVERTED sign:
   * `getTimezoneOffset()` returns 60 for UTC-01:00 and -60 for UTC+01:00.
   */
  private formatTimezone(): string {
    const offsetMinutes = new Date().getTimezoneOffset();
    const sign = offsetMinutes <= 0 ? '+' : '-';
    const absolute = Math.abs(offsetMinutes);
    const hours = Math.floor(absolute / 60);
    const minutes = absolute % 60;
    return `UTC${sign}${this.pad2(hours)}:${this.pad2(minutes)}`;
  }

  private pad2(value: number): string {
    return String(value).padStart(2, '0');
  }

  private readPlugins(): string[] {
    if (typeof navigator === 'undefined' || !navigator.plugins) {
      return [];
    }
    // Empty on most modern browsers — send as-is, never fabricate.
    return Array.from(navigator.plugins).map((plugin) => plugin.name);
  }

  private readWindowWidth(): number {
    return typeof window !== 'undefined' ? window.innerWidth : 0;
  }

  private readWindowHeight(): number {
    return typeof window !== 'undefined' ? window.innerHeight : 0;
  }

  private newUuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Extremely unlikely fallback (no Web Crypto) — keep the header valid.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const rand = (Math.random() * 16) | 0;
      const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  private readStoredDeviceId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      return window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeStoredDeviceId(deviceId: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
    } catch {
      // no-op — an in-memory id is used for the session.
    }
  }

  /**
   * base64(JSON), trimming trailing `plugins` entries (the only unbounded field)
   * until the encoded header fits within the 4 KB cap.
   */
  private encodeWithinCap(data: ClientData): string {
    const payload: ClientData = { ...data, plugins: [...data.plugins] };
    let encoded = this.encode(payload);

    while (this.byteLength(encoded) > MAX_HEADER_BYTES && payload.plugins.length > 0) {
      payload.plugins.pop();
      encoded = this.encode(payload);
    }

    return encoded;
  }

  private encode(data: ClientData): string {
    const json = JSON.stringify(data);
    // UTF-8 safe base64 (raw btoa throws on non-Latin1 characters).
    return btoa(
      encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))),
    );
  }

  private byteLength(base64: string): number {
    return base64.length;
  }
}
